import {
  Bot,
  CheckCircle2,
  Clock,
  FileSearch,
  FileText,
  Loader2,
  Mail,
  Send,
  UserCheck,
  XCircle,
} from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';

import type { MiniTortClaimDetail, PipelineStatus } from './types';
import { MiniTortStage } from './types';

interface MiniTortStageGuidanceProps {
  claim: MiniTortClaimDetail;
  status: PipelineStatus | undefined;
  className?: string;
}

interface GuidanceInfo {
  icon: React.ReactNode;
  title: string;
  description: string;
  owner: 'system' | 'staff' | 'waiting';
  ownerLabel: string;
  actions: string[];
}

function getGuidance(claim: MiniTortClaimDetail, status: PipelineStatus | undefined): GuidanceInfo {
  switch (claim.stage) {
    case MiniTortStage.PROCESSING_SUBMISSION:
      return {
        icon: <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />,
        title: 'System is processing this submission',
        description: 'Downloading attachments, running AI extraction, verifying documents. No action needed.',
        owner: 'system',
        ownerLabel: 'Automated',
        actions: [],
      };

    case MiniTortStage.REVIEWING_CLAIM:
      return {
        icon: <UserCheck className="h-5 w-5 text-indigo-500" />,
        title: 'Paralegal review required',
        description: status?.has_unresolved_blockers
          ? 'Resolve blocker flags before this claim can advance.'
          : 'Review extracted data, verify prerequisites, then approve or request materials.',
        owner: 'staff',
        ownerLabel: 'Staff Action',
        actions: status?.has_unresolved_blockers
          ? ['Resolve all blocker flags', 'Then approve or request materials']
          : ['Verify coverage and police report data', 'Approve to advance, or request materials'],
      };

    case MiniTortStage.NEED_ADDITIONAL_MATERIALS:
      return {
        icon: <Mail className="h-5 w-5 text-orange-500" />,
        title: 'Waiting for claimant response',
        description: 'System is sending up to 4 reminders on a 2-day cadence. Claim returns to review when materials arrive.',
        owner: 'waiting',
        ownerLabel: 'Claimant',
        actions: ['Monitor for claimant response', 'Mark materials received when ready'],
      };

    case MiniTortStage.RETRIEVING_POLICE_REPORT:
      return {
        icon: <FileSearch className="h-5 w-5 text-orange-500" />,
        title: 'Police report retrieval in progress',
        description: 'Standard police report infrastructure is handling retrieval. 3P coverage will be extracted when report arrives.',
        owner: 'system',
        ownerLabel: 'Automated',
        actions: [],
      };

    case MiniTortStage.GENERATING_DEMAND:
      return {
        icon: <FileText className="h-5 w-5 text-amber-500" />,
        title: 'Demand package being generated',
        description: 'System is building the demand letter using 3P carrier info. A submit-demand task will be created when ready.',
        owner: 'system',
        ownerLabel: 'Automated',
        actions: ['Submit-demand task will appear when generation completes'],
      };

    case MiniTortStage.DEMAND_SUBMITTED: {
      const chase = status?.chase_progress;
      const stepLabels = ['Day 7 follow-up', 'Day 14 follow-up', 'Day 21 follow-up', 'Day 30 escalation'];
      const nextStep = chase ? stepLabels[chase.current_step_index] ?? 'Next step' : 'Follow-up';
      return {
        icon: <Send className="h-5 w-5 text-amber-500" />,
        title: 'Demand sent \u2014 chase in progress',
        description: chase
          ? `Chase step ${chase.current_step_index + 1}/${chase.total_steps}. Next: ${nextStep}.`
          : 'Insurer follow-up emails are being sent on a scheduled cadence.',
        owner: 'system',
        ownerLabel: 'Chase Workflow',
        actions: ['Advance to Insurer Acknowledged when insurer confirms receipt', 'Or advance directly to Payment Issued'],
      };
    }

    case MiniTortStage.INSURER_ACKNOWLEDGED:
      return {
        icon: <CheckCircle2 className="h-5 w-5 text-emerald-500" />,
        title: 'Insurer confirmed receipt',
        description: 'Waiting for payment from the insurer. Advance when payment is issued.',
        owner: 'staff',
        ownerLabel: 'Staff Action',
        actions: ['Record payment when insurer issues it'],
      };

    case MiniTortStage.PAYMENT_ISSUED:
      return {
        icon: <CheckCircle2 className="h-5 w-5 text-emerald-500" />,
        title: 'Payment issued by insurer',
        description: 'Mark as "Check Mailed" when the payment check is sent to the claimant.',
        owner: 'staff',
        ownerLabel: 'Staff Action',
        actions: ['Advance to Payment En Route when check is mailed'],
      };

    case MiniTortStage.PAYMENT_EN_ROUTE: {
      const onHold = status?.auto_close_on_hold;
      return {
        icon: <Clock className="h-5 w-5 text-emerald-500" />,
        title: onHold ? 'Auto-close paused' : 'Auto-close timer running',
        description: status?.auto_close_deadline_at
          ? `Claim will auto-close on ${new Date(status.auto_close_deadline_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.`
          : 'Check is on the way to claimant. Claim will auto-close after a configurable delay.',
        owner: 'system',
        ownerLabel: onHold ? 'Paused' : 'Auto-Close',
        actions: onHold ? ['Resume auto-close or close manually'] : ['Hold auto-close if needed, or let it run'],
      };
    }

    case MiniTortStage.CLOSED:
      return {
        icon: <CheckCircle2 className="h-5 w-5 text-zinc-400" />,
        title: 'Claim closed',
        description: 'This claim has been completed successfully.',
        owner: 'system',
        ownerLabel: 'Complete',
        actions: [],
      };

    case MiniTortStage.REJECTED:
      return {
        icon: <XCircle className="h-5 w-5 text-red-500" />,
        title: 'Claim rejected',
        description: claim.rejection_reason ?? 'This claim was determined to be ineligible.',
        owner: 'system',
        ownerLabel: 'Terminal',
        actions: [],
      };

    default:
      return {
        icon: <Clock className="h-5 w-5 text-zinc-400" />,
        title: 'Unknown stage',
        description: '',
        owner: 'system',
        ownerLabel: '-',
        actions: [],
      };
  }
}

const OWNER_STYLES = {
  system: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  staff: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  waiting: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
} as const;

export const MiniTortStageGuidance: React.FC<MiniTortStageGuidanceProps> = ({
  claim,
  status,
  className,
}) => {
  const guidance = getGuidance(claim, status);

  return (
    <div
      className={cn(
        'rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">{guidance.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
              {guidance.title}
            </h3>
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase',
                OWNER_STYLES[guidance.owner],
              )}
            >
              {guidance.owner === 'system' && <Bot className="h-3 w-3" />}
              {guidance.owner === 'staff' && <UserCheck className="h-3 w-3" />}
              {guidance.ownerLabel}
            </span>
          </div>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{guidance.description}</p>
          {guidance.actions.length > 0 && (
            <ul className="mt-2 space-y-1">
              {guidance.actions.map((action) => (
                <li key={action} className="flex items-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-400">
                  <span className="h-1 w-1 shrink-0 rounded-full bg-zinc-400" />
                  {action}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
